import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Loader2, Save, CheckCircle2 } from "lucide-react";
import { useFieldAccess, useMyFieldAccess, FlacScopeBanner } from "../hooks/useFieldAccess";
import FlacField from "../components/FlacField";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = {
  client_type: "PF",
  cnp: "",
  cui: "",
  first_name: "",
  last_name: "",
  full_name: "",
  company_name: "",
  phone_mobile: "",
  phone_fixed: "",
  email: "",
  county: "",
  locality: "",
  street: "",
  postal_code: "",
  address_raw: "",
  representative_name: "",
  representative_cnp: "",
  representative_phone: "",
  representative_email: "",
  // Iter41 — extended legal representative
  legal_representative_id_series: "",
  legal_representative_role: "",
  // Iter43.7 — Final 5 fields for catalog parity 121/121
  id_series: "",
  legal_representative_name: "",
  legal_representative_cnp: "",
  consent_marketing: false,
  consent_personal_data: false,
};

const ClientForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Iter48 — Dirty tracking + save feedback
  const initialFormRef = useRef(EMPTY);
  const [justSaved, setJustSaved] = useState(false);
  // Iter40b — FLAC scope (for both create and edit modes)
  const scope = useMyFieldAccess();
  const flac = useFieldAccess(scope, { mode: "scope" });

  // Iter48 — `isDirty` computed by shallow comparison vs. last persisted snapshot.
  const isDirty = useMemo(() => {
    const ref = initialFormRef.current;
    return Object.keys(EMPTY).some((k) => {
      const a = ref[k];
      const b = form[k];
      // Normalize "" vs null for comparison
      if ((a === "" || a == null) && (b === "" || b == null)) return false;
      return a !== b;
    });
  }, [form]);

  // Iter48 — Browser-level guard (tab close / hard refresh / external link).
  // NOTE: in-app react-router <Link> navigation isn't blocked here because the
  // app uses BrowserRouter (declarative); useBlocker requires a Data Router.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      await loadClient();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // Iter46 — exported as helper so handleSubmit can re-sync after PUT
  const loadClient = async () => {
    try {
      const r = await axios.get(`${API_URL}/clients/${id}`);
      const merged = Object.fromEntries(
        Object.keys(EMPTY).map((k) => {
          const raw = r.data[k];
          if (typeof EMPTY[k] === "boolean") return [k, raw === true];
          return [k, raw ?? ""];
        })
      );
      merged.client_type = r.data.client_type || "PF";
      setForm(merged);
      // Iter48 — snapshot fresh server state as new clean baseline
      initialFormRef.current = merged;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Client negăsit");
      navigate("/clients");
    }
  };

  const update = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isPF = form.client_type === "PF";
    if (isPF && !(form.first_name || form.last_name || form.full_name)) {
      toast.error("Numele clientului este obligatoriu");
      return;
    }
    if (!isPF && !form.company_name) {
      toast.error("Numele firmei este obligatoriu");
      return;
    }
    setSaving(true);
    try {
      // Strip empty strings → null so backend doesn't store "".
      // Booleans must remain (true/false), not coerced.
      let payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => {
          if (typeof v === "boolean") return [k, v];
          return [k, v === "" ? null : v];
        })
      );
      // client_type must stay
      payload.client_type = form.client_type;
      // Iter40b — drop fields user can't write before sending (avoids 403)
      payload = flac.stripNonWritable(payload, "client");
      // client_type is always allowed (no FLAC rule on it)
      if (!payload.client_type) payload.client_type = form.client_type;
      if (isEdit) {
        await axios.put(`${API_URL}/clients/${id}`, payload);
        toast.success("Datele clientului au fost actualizate cu succes");
        // Iter46 — rămâne pe formular și re-sincronizează datele (fără navigate)
        await loadClient();
        // Iter48 — flash green tick for 2 seconds
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } else {
        const r = await axios.post(`${API_URL}/clients`, payload);
        toast.success("Client creat");
        navigate(`/clients/${r.data.id}`);
      }
    } catch (err) {
      // Iter49 — handle structured 409 duplicate_identity payload
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "duplicate_identity") {
        toast.error(detail.message, {
          duration: 8000,
          action: detail.existing_client_id ? {
            label: "Deschide clientul existent",
            onClick: () => navigate(`/clients/${detail.existing_client_id}`),
          } : undefined,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Eroare la salvare"));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const isPF = form.client_type === "PF";

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="client-form">
      <div className="flex items-center gap-3">
        <Link to={isEdit ? `/clients/${id}` : "/clients"}>
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? "Editează Client" : "Client Nou"}
          </h1>
          <p className="text-slate-500 text-sm">
            {isEdit ? "Modifică datele clientului existent" : "Completează datele pentru un client nou"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Iter40b — FLAC scope banner */}
        <FlacScopeBanner flac={flac} entity="acest formular client" />

        {/* Type */}
        <Card>
          <CardHeader><CardTitle className="text-base">Tip Client</CardTitle></CardHeader>
          <CardContent>
            <Select value={form.client_type} onValueChange={(v) => setForm((p) => ({ ...p, client_type: v }))}>
              <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PF">Persoană Fizică</SelectItem>
                <SelectItem value="PJ">Persoană Juridică</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Identity */}
        <Card>
          <CardHeader><CardTitle className="text-base">Identitate</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isPF ? (
              <>
                <div className="space-y-2">
                  <Label>Nume</Label>
                  <FlacField name="last_name" entity="client" flac={flac}>
                    <Input value={form.last_name} onChange={update("last_name")} data-testid="last-name" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Prenume</Label>
                  <FlacField name="first_name" entity="client" flac={flac}>
                    <Input value={form.first_name} onChange={update("first_name")} data-testid="first-name" />
                  </FlacField>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Nume complet (opțional)</Label>
                  <FlacField name="full_name" entity="client" flac={flac}>
                    <Input value={form.full_name} onChange={update("full_name")} placeholder="Se completează automat" data-testid="full-name" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>CNP</Label>
                  <FlacField name="cnp" entity="client" flac={flac}>
                    <Input value={form.cnp} onChange={update("cnp")} data-testid="cnp" maxLength={13} />
                  </FlacField>
                </div>
                {/* Iter43.7 — Serie buletin (PF) */}
                <div className="space-y-2">
                  <Label>Serie buletin</Label>
                  <FlacField name="id_series" entity="client" flac={flac}>
                    <Input value={form.id_series} onChange={update("id_series")} data-testid="id_series" placeholder="ex: RT 123456" maxLength={20} />
                  </FlacField>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2 md:col-span-2">
                  <Label>Nume firmă *</Label>
                  <FlacField name="company_name" entity="client" flac={flac}>
                    <Input value={form.company_name} onChange={update("company_name")} data-testid="company-name" required />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>CUI</Label>
                  <FlacField name="cui" entity="client" flac={flac}>
                    <Input value={form.cui} onChange={update("cui")} data-testid="cui" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant — Nume</Label>
                  <FlacField name="representative_name" entity="client" flac={flac}>
                    <Input value={form.representative_name} onChange={update("representative_name")} data-testid="rep-name" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant — CNP</Label>
                  <FlacField name="representative_cnp" entity="client" flac={flac}>
                    <Input value={form.representative_cnp} onChange={update("representative_cnp")} data-testid="rep-cnp" maxLength={13} />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant — Telefon</Label>
                  <FlacField name="representative_phone" entity="client" flac={flac}>
                    <Input value={form.representative_phone} onChange={update("representative_phone")} data-testid="rep-phone" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant — Email</Label>
                  <FlacField name="representative_email" entity="client" flac={flac}>
                    <Input type="email" value={form.representative_email} onChange={update("representative_email")} data-testid="rep-email" />
                  </FlacField>
                </div>
                {/* Iter41 — extended legal representative fields */}
                <div className="space-y-2">
                  <Label>Reprezentant legal — Nume</Label>
                  <FlacField name="legal_representative_name" entity="client" flac={flac}>
                    <Input value={form.legal_representative_name} onChange={update("legal_representative_name")} data-testid="legal_representative_name" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant legal — CNP</Label>
                  <FlacField name="legal_representative_cnp" entity="client" flac={flac}>
                    <Input value={form.legal_representative_cnp} onChange={update("legal_representative_cnp")} data-testid="legal_representative_cnp" maxLength={13} />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant legal — Serie buletin</Label>
                  <FlacField name="legal_representative_id_series" entity="client" flac={flac}>
                    <Input value={form.legal_representative_id_series} onChange={update("legal_representative_id_series")} data-testid="legal-rep-id-series" placeholder="ex: RT 123456" />
                  </FlacField>
                </div>
                <div className="space-y-2">
                  <Label>Reprezentant legal — Funcție</Label>
                  <FlacField name="legal_representative_role" entity="client" flac={flac}>
                    <Input value={form.legal_representative_role} onChange={update("legal_representative_role")} data-testid="legal-rep-role" placeholder="Administrator / Director / Asociat" />
                  </FlacField>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Telefon mobil</Label>
              <FlacField name="phone_mobile" entity="client" flac={flac}>
                <Input value={form.phone_mobile} onChange={update("phone_mobile")} data-testid="phone-mobile" />
              </FlacField>
            </div>
            <div className="space-y-2">
              <Label>Telefon fix</Label>
              <FlacField name="phone_fixed" entity="client" flac={flac}>
                <Input value={form.phone_fixed} onChange={update("phone_fixed")} data-testid="phone-fixed" />
              </FlacField>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Email</Label>
              <FlacField name="email" entity="client" flac={flac}>
                <Input type="email" value={form.email} onChange={update("email")} data-testid="email" />
              </FlacField>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardHeader><CardTitle className="text-base">Adresă</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Județ</Label>
              <FlacField name="county" entity="client" flac={flac}>
                <Input value={form.county} onChange={update("county")} data-testid="county" />
              </FlacField>
            </div>
            <div className="space-y-2">
              <Label>Localitate</Label>
              <FlacField name="locality" entity="client" flac={flac}>
                <Input value={form.locality} onChange={update("locality")} data-testid="locality" />
              </FlacField>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Stradă & nr.</Label>
              <FlacField name="street" entity="client" flac={flac}>
                <Input value={form.street} onChange={update("street")} data-testid="street" />
              </FlacField>
            </div>
            <div className="space-y-2">
              <Label>Cod poștal</Label>
              <FlacField name="postal_code" entity="client" flac={flac}>
                <Input value={form.postal_code} onChange={update("postal_code")} data-testid="postal-code" />
              </FlacField>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Adresă completă (text liber)</Label>
              <FlacField name="address_raw" entity="client" flac={flac}>
                <Input value={form.address_raw} onChange={update("address_raw")} placeholder="ex: Str. ABC 12, Bl. A2, Sc. 1, Ap. 5" data-testid="address-raw" />
              </FlacField>
            </div>
          </CardContent>
        </Card>

        {/* Iter43.7 — GDPR consents (scalar flags on Client). For granular GDPR history per consent_type, see <ClientConsents /> on the ClientDetail page. */}
        <Card data-testid="client-consents-card">
          <CardHeader>
            <CardTitle className="text-base">Consimțăminte GDPR (flag-uri scalare)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500 -mt-2">
              Aceste 2 toggle-uri reflectă valorile din importere legacy. Pentru istoric GDPR complet
              (granted/revoked/source/evidence per categorie) folosește secțiunea „Consimțăminte" din pagina de Detalii client.
            </p>
            <div className="flex items-center gap-2">
              <FlacField name="consent_marketing" entity="client" flac={flac}>
                <Checkbox
                  id="consent_marketing"
                  checked={!!form.consent_marketing}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, consent_marketing: !!v }))}
                  data-testid="consent_marketing"
                />
              </FlacField>
              <Label htmlFor="consent_marketing" className="cursor-pointer">Consimțământ marketing</Label>
            </div>
            <div className="flex items-center gap-2">
              <FlacField name="consent_personal_data" entity="client" flac={flac}>
                <Checkbox
                  id="consent_personal_data"
                  checked={!!form.consent_personal_data}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, consent_personal_data: !!v }))}
                  data-testid="consent_personal_data"
                />
              </FlacField>
              <Label htmlFor="consent_personal_data" className="cursor-pointer">Consimțământ prelucrare date personale</Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 items-center">
          {/* Iter48 — Dirty/saved indicator */}
          {isEdit && (
            <span className="text-sm flex items-center gap-1.5" data-testid="dirty-status">
              {justSaved ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" data-testid="just-saved-icon" />
                  <span className="text-emerald-700">Salvat</span>
                </>
              ) : isDirty ? (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" data-testid="dirty-dot" />
                  <span className="text-amber-700">Modificări nesalvate</span>
                </>
              ) : null}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            data-testid="cancel-btn"
            onClick={(e) => {
              if (isDirty && !window.confirm("Aveți modificări nesalvate. Părăsiți pagina fără să salvați?")) {
                e.preventDefault();
                return;
              }
              navigate(isEdit ? `/clients/${id}` : "/clients");
            }}
          >
            Anulează
          </Button>
          <Button
            type="submit"
            disabled={saving || (isEdit && !isDirty)}
            data-testid="save-client-btn"
            className={isDirty && isEdit ? "ring-2 ring-amber-400 ring-offset-1" : ""}
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? "Salvează modificările" : "Creează client"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
